import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Stack,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import Swal from "sweetalert2";
import moment from "moment";

import { auth, db } from "../firebase";
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc,
} from "firebase/firestore";

export default function ViewPurchase() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
  });

  const [propertyId, setPropertyId] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [open, setOpen] = useState(false);

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* ----------------------------------------
     FETCH PROPERTY ID FROM authroles
  ---------------------------------------- */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;

      const q = query(
        collection(db, "authroles"),
        where("uid", "==", user.uid)
      );

      const snap = await getDocs(q);
      if (!snap.empty) {
        setPropertyId(snap.docs[0].data().propertyId);
      }
    });

    return () => unsub();
  }, []);

  /* ----------------------------------------
     FETCH PURCHASES
  ---------------------------------------- */
  useEffect(() => {
    if (!propertyId) return;

    const fetchPurchases = async () => {
      try {
        const q = query(
          collection(db, "purchases"),
          where("status", "==", "Active"),
          where("propertyId", "==", propertyId),
          orderBy("created", "desc")
        );

        const snap = await getDocs(q);

        const raw = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        // dealer mapping
        const dealerIds = [...new Set(raw.map((r) => r.dealerId).filter(Boolean))];

        const dealerSnaps = await Promise.all(
          dealerIds.map((id) => getDoc(doc(db, "dealers", id)))
        );

        const dealerMap = {};
        dealerSnaps.forEach((d) => {
          if (d.exists()) dealerMap[d.id] = d.data().name;
        });

        const finalData = raw.map((r) => ({
          ...r,
          dealername: dealerMap[r.dealerId] || "Unknown",
        }));

        console.log(finalData);
        setPurchases(finalData);
        setFiltered(finalData);
      } catch (err) {
        console.log(err.message );
        
        Toast.fire({ icon: "error", title: err.message });
      }
    };

    fetchPurchases();
  }, [propertyId]);

  /* ----------------------------------------
     SEARCH
  ---------------------------------------- */
  useEffect(() => {
    if (!searchText) {
      setFiltered(purchases);
    } else {
      setFiltered(
        purchases.filter((p) =>
          p.dealername.toLowerCase().includes(searchText.toLowerCase())
        )
      );
    }
  }, [searchText, purchases]);

  /* ----------------------------------------
     VIEW ITEMS
  ---------------------------------------- */
  const handleView = (purchaseId) => {
    const q = query(
      collection(db, "purchasesitems"),
      where("purchaseid", "==", purchaseId)
    );

    onSnapshot(q, (snap) => {
      setPurchaseItems(
        snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
      setOpen(true);
    });
  };

  /* ----------------------------------------
     DELETE
  ---------------------------------------- */
  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "purchases", id));
    Toast.fire({ icon: "success", title: "Purchase deleted" });
    setPurchases((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <Box className=" p-3">
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h5" color="white">View Purchases</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          href="add-purchase"
        >
          Add Purchase
        </Button>
      </Stack>

      <Card>
        <CardContent>
          <TextField
            label="Search Dealer"
            size="small"
            fullWidth
            sx={{ mb: 2 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sr</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Purchase No</TableCell>
                  <TableCell>Dealer</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Remaining</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filtered
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, i) => (
                    <TableRow key={row.id}>
                      <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                      <TableCell>
                        {moment(row.purchaseDate).format("DD-MM-YYYY")}
                      </TableCell>
                      <TableCell>{row.purchaseNo}</TableCell>
                      <TableCell>{row.dealername}</TableCell>
                      <TableCell>{row.grandtotal}</TableCell>
                      <TableCell>{row.paidamount}</TableCell>
                      <TableCell>{row.remaining}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="success"
                          onClick={() => handleView(row.id)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(row.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, n) => setPage(n)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* VIEW ITEMS DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>Purchase Items</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Rate</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>GST</TableCell>
                <TableCell>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchaseItems.map((it) => (
                <TableRow key={it.id}>
                  <TableCell>{it.itemname}</TableCell>
                  <TableCell>{it.rate}</TableCell>
                  <TableCell>{it.qty}</TableCell>
                  <TableCell>{it.discount}</TableCell>
                  <TableCell>{it.gst_amount}</TableCell>
                  <TableCell>{it.total_amount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={() => setOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
